import { Box, Typography, Skeleton, LinearProgress, alpha } from '@mui/material';
import { colors, status } from '../../theme';

export const fmt = (n) => n ? n.toLocaleString('vi-VN', { maximumFractionDigits: 0 }) : '—';
export const fmtPct = (n, d = 1) => n != null ? `${n >= 0 ? '+' : ''}${n.toFixed(d)}%` : '—';
export const fmtDec = (n, d = 1) => n != null ? Number(n).toFixed(d) : '—';
export const SETUP_KEYS = ['BREAKOUT_CONFIRMED', 'SUCCESSFUL_TEST', 'SHAKEOUT', 'VOL_DRYUP_READY', 'NO_SUPPLY_CONFIRMED', 'NO_SUPPLY', 'ABSORPTION_DOWN', 'STOPPING_VOLUME', 'OTHER'];

// Maps backend tone names → theme status tokens (keeps { color, bg, border } shape for consumers)
const TONE_MAP = {
  success: { color: status.positive.main, bg: status.positive.bg, border: status.positive.border },
  info:    { color: status.info.main,     bg: status.info.bg,     border: status.info.border },
  warning: { color: status.warning.main,  bg: status.warning.bg,  border: status.warning.border },
  danger:  { color: status.negative.main, bg: status.negative.bg, border: status.negative.border },
  accent:  { color: status.purple.main,   bg: status.purple.bg,   border: status.purple.border },
  muted:   { color: status.neutral.main,  bg: status.neutral.bg,  border: status.neutral.border },
};

export const getDisplayTone = (tone) => TONE_MAP[tone] || TONE_MAP.muted;

export const getDisplayText = (item, t, fallback = '—', keyFields = ['key', 'label_key']) => {
  if (!item) return fallback;
  if (item.text != null) return item.text;
  const fields = Array.isArray(keyFields) ? keyFields : [keyFields];
  for (const field of fields) {
    const key = item[field];
    if (!key) continue;
    const translated = t(key, item.params || {});
    if (translated !== key) return translated;
  }
  return item.fallback_text ?? fallback;
};

export const thSx = {
  fontSize: '0.68rem', fontWeight: 600, color: colors.gray[500],
  letterSpacing: '0.03em', textTransform: 'uppercase', py: 1.2, px: 2,
  borderBottom: `1px solid ${colors.gray[200]}`, bgcolor: colors.gray[50],
  whiteSpace: 'nowrap', lineHeight: 1.4,
};

export const tdSx = {
  py: 0.8, px: 2, fontSize: '0.78rem',
  borderBottom: `1px solid ${colors.gray[100]}`,
};

export const trSx = (i, hoverColor = colors.info.main) => ({
  '&:nth-of-type(even)': { bgcolor: colors.gray[50] },
  '&:hover': { bgcolor: alpha(hoverColor, 0.04) },
  transition: 'background 0.15s',
});

export const scoreColor = (s) => {
  if (s == null) return colors.gray[400];
  if (s >= 80) return colors.success.main;
  if (s >= 60) return colors.info.main;
  if (s >= 40) return colors.warning.main;
  return colors.error.main;
};

export const gradeColor = (g) => ({
  A: colors.success.main, B: colors.info.main, C: colors.warning.main, D: colors.gray[400],
}[g] || colors.gray[400]);
export const regimeColor = (r) => ({
  RISK_ON: colors.success.main, SIDEWAY: colors.warning.main, RISK_OFF: colors.error.main,
}[r] || colors.gray[400]);

export const SETUP_COLORS = [colors.info.main, colors.success.main, colors.warning.main, colors.error.main, status.purple.main, '#ec4899', colors.superStock.main, colors.priceAction.main];

export const Card = ({ children, sx = {} }) => (
  <Box sx={{ bgcolor: colors.background.paper, borderRadius: 1.5, border: `1px solid ${colors.gray[200]}`, p: 2.5, ...sx }}>
    {children}
  </Box>
);

export const CardTitle = ({ children, icon: Icon, action }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
      {Icon && <Icon sx={{ fontSize: 16, color: colors.gray[500] }} />}
      <Typography sx={{ fontSize: '0.78rem', fontWeight: 700, color: colors.gray[600] }}>{children}</Typography>
    </Box>
    {action}
  </Box>
);

export const Tile = ({ label, value, sub, color, Icon }) => (
  <Box sx={{
    position: 'relative', overflow: 'hidden',
    borderRadius: 1.5, border: `1px solid ${alpha(color, 0.15)}`,
    bgcolor: colors.background.paper, p: '14px 16px', transition: 'all 0.2s',
    '&:hover': { borderColor: alpha(color, 0.3), boxShadow: `0 4px 16px ${alpha(color, 0.08)}`, transform: 'translateY(-1px)' },
  }}>
    <Box sx={{ position: 'absolute', top: 0, right: 0, width: 64, height: 64, background: `radial-gradient(circle at top right, ${alpha(color, 0.06)} 0%, transparent 70%)` }} />
    <Box sx={{ position: 'relative', display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
      <Box sx={{ width: 38, height: 38, borderRadius: 2, flexShrink: 0, background: `linear-gradient(135deg, ${color} 0%, ${alpha(color, 0.7)} 100%)`, display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: `0 3px 8px ${alpha(color, 0.25)}` }}>
        <Icon sx={{ fontSize: 20, color: colors.background.paper }} />
      </Box>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography sx={{ fontSize: '0.7rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px', color: colors.gray[400], lineHeight: 1, mb: 0.5 }} noWrap>{label}</Typography>
        <Typography sx={{ fontSize: String(value).length > 6 ? '1.25rem' : '1.75rem', fontWeight: 900, color, lineHeight: 1.2, fontFamily: 'monospace' }} noWrap>{value}</Typography>
        {sub && <Typography sx={{ fontSize: '0.68rem', color: colors.gray[400], mt: 0.4 }} noWrap>{sub}</Typography>}
      </Box>
    </Box>
  </Box>
);

export const ScoreBar = ({ score, maxScore = 100 }) => {
  const pct = Math.min(100, Math.max(0, (score / maxScore) * 100));
  const color = scoreColor(score);
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
      <Box sx={{ flex: 1, height: 6, bgcolor: alpha(color, 0.1), borderRadius: 1, overflow: 'hidden' }}>
        <Box sx={{ width: `${pct}%`, height: '100%', bgcolor: color, borderRadius: 1, transition: 'width 0.5s ease' }} />
      </Box>
      <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, color, minWidth: 30, textAlign: 'right' }}>
        {Math.round(score)}
      </Typography>
    </Box>
  );
};

export const MiniScoreBar = ({ label, score, icon: Icon }) => {
  const color = scoreColor(score);
  return (
    <Box sx={{ mb: 1.2 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          {Icon && <Icon sx={{ fontSize: 12, color: colors.gray[400] }} />}
          <Typography sx={{ fontSize: '0.68rem', fontWeight: 600, color: colors.gray[500] }}>{label}</Typography>
        </Box>
        <Typography sx={{ fontSize: '0.72rem', fontWeight: 800, color, fontFamily: 'monospace' }}>{fmtDec(score, 1)}</Typography>
      </Box>
      <LinearProgress
        variant="determinate"
        value={Math.min(100, Math.max(0, score))}
        sx={{
          height: 5, borderRadius: 1, bgcolor: alpha(color, 0.1),
          '& .MuiLinearProgress-bar': { bgcolor: color, borderRadius: 1 },
        }}
      />
    </Box>
  );
};

export const EmptyState = ({ Icon, title }) => (
  <Box sx={{ py: 5, px: 3, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1.5 }}>
    <Box sx={{ width: 56, height: 56, borderRadius: '50%', bgcolor: colors.gray[50], border: `1.5px dashed ${colors.gray[200]}`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      {Icon && <Icon sx={{ fontSize: 24, color: colors.gray[300] }} />}
    </Box>
    <Typography sx={{ fontSize: '0.84rem', fontWeight: 600, color: colors.gray[400] }}>{title}</Typography>
  </Box>
);

export const TileSkeleton = () => (
  <Box sx={{ borderRadius: 1.5, border: `1px solid ${colors.gray[100]}`, bgcolor: colors.background.paper, p: '14px 16px', display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
    <Skeleton variant="rounded" width={38} height={38} sx={{ borderRadius: 2, flexShrink: 0 }} />
    <Box sx={{ flex: 1 }}>
      <Skeleton variant="text" width="50%" height={14} sx={{ mb: 0.5 }} />
      <Skeleton variant="text" width="40%" height={28} />
    </Box>
  </Box>
);

export const SectorTableSkeleton = () => (
  <Box sx={{ borderRadius: 1.5, border: `1px solid ${colors.gray[200]}`, bgcolor: colors.background.paper, overflow: 'hidden', mb: 2 }}>
    <Box sx={{ px: 2.5, py: 1.5, borderBottom: `1px solid ${colors.gray[100]}`, display: 'flex', gap: 1.5, alignItems: 'center' }}>
      {[60, 80, 70, 50].map((width, index) => <Skeleton key={index} variant="rounded" width={width} height={22} sx={{ borderRadius: 3 }} />)}
    </Box>
    <Box component="table" sx={{ width: '100%', borderCollapse: 'collapse' }}>
      <Box component="thead">
        <Box component="tr" sx={{ bgcolor: colors.gray[50] }}>
          {[30, 100, 50, 50, 60, 70, 60].map((width, index) => (
            <Box key={index} component="td" sx={{ py: 1, px: 2 }}><Skeleton width={width} height={12} /></Box>
          ))}
        </Box>
      </Box>
      <Box component="tbody">
        {Array.from({ length: 6 }).map((_, rowIndex) => (
          <Box key={rowIndex} component="tr" sx={{ bgcolor: rowIndex % 2 === 0 ? colors.background.paper : colors.gray[50] }}>
            {[30, 100, 50, 50, 60, 70, 60].map((width, cellIndex) => (
              <Box key={cellIndex} component="td" sx={{ py: 1, px: 2 }}><Skeleton width={width * (0.7 + Math.random() * 0.3)} height={12} /></Box>
            ))}
          </Box>
        ))}
      </Box>
    </Box>
  </Box>
);

export const RecommendationsSkeleton = () => (
  <Box sx={{ borderRadius: 1.5, border: `1px solid ${colors.gray[200]}`, bgcolor: colors.background.paper, overflow: 'hidden', mb: 2 }}>
    <Box sx={{ px: 2.5, py: 1.5, borderBottom: `1px solid ${colors.gray[100]}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
      <Skeleton width={120} height={16} />
      <Box sx={{ display: 'flex', gap: 1 }}>
        {[0, 1, 2].map((index) => <Skeleton key={index} variant="rounded" width={80} height={36} sx={{ borderRadius: 1.5 }} />)}
      </Box>
    </Box>
    <Box component="table" sx={{ width: '100%', borderCollapse: 'collapse' }}>
      <Box component="thead">
        <Box component="tr" sx={{ bgcolor: colors.gray[50] }}>
          {[70, 120, 80, 60, 60, 60, 60, 50].map((width, index) => (
            <Box key={index} component="td" sx={{ py: 1, px: 2 }}><Skeleton width={width} height={12} /></Box>
          ))}
        </Box>
      </Box>
      <Box component="tbody">
        {Array.from({ length: 4 }).map((_, rowIndex) => (
          <Box key={rowIndex} component="tr" sx={{ bgcolor: rowIndex % 2 === 0 ? colors.background.paper : colors.gray[50] }}>
            <Box component="td" sx={{ py: 1.2, px: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
                <Skeleton variant="circular" width={8} height={8} />
                <Skeleton width={40} height={12} />
              </Box>
            </Box>
            <Box component="td" sx={{ py: 1.2, px: 2 }}><Box><Skeleton width={44} height={14} sx={{ mb: 0.3 }} /><Skeleton width={56} height={10} /></Box></Box>
            {[80, 60, 60, 60, 60, 50].map((width, cellIndex) => (
              <Box key={cellIndex} component="td" sx={{ py: 1.2, px: 2 }}><Skeleton width={width * (0.7 + Math.random() * 0.3)} height={12} /></Box>
            ))}
          </Box>
        ))}
      </Box>
    </Box>
  </Box>
);

export const MainTableSkeleton = () => (
  <Box sx={{ borderRadius: 1.5, border: `1px solid ${colors.gray[200]}`, bgcolor: colors.background.paper, overflow: 'hidden' }}>
    <Box sx={{ px: 2, py: 1.5, borderBottom: `1px solid ${colors.gray[100]}`, display: 'flex', gap: 1, alignItems: 'center' }}>
      {[60, 70, 55, 65].map((width, index) => <Skeleton key={index} variant="rounded" width={width} height={28} sx={{ borderRadius: 3 }} />)}
      <Box sx={{ flex: 1 }} />
      <Skeleton variant="rounded" width={160} height={32} sx={{ borderRadius: 1 }} />
    </Box>
    <Box component="table" sx={{ width: '100%', borderCollapse: 'collapse' }}>
      <Box component="thead">
        <Box component="tr" sx={{ bgcolor: colors.gray[50] }}>
          {[24, 60, 80, 80, 60, 50, 70, 55, 45, 60, 40, 24].map((width, index) => (
            <Box key={index} component="td" sx={{ py: 1, px: 2 }}><Skeleton width={width} height={12} /></Box>
          ))}
        </Box>
      </Box>
      <Box component="tbody">
        {Array.from({ length: 6 }).map((_, rowIndex) => (
          <Box key={rowIndex} component="tr" sx={{ bgcolor: rowIndex % 2 === 0 ? colors.background.paper : colors.gray[50] }}>
            <Box component="td" sx={{ py: 1.4, px: 2 }}><Skeleton width={16} height={12} /></Box>
            <Box component="td" sx={{ py: 1.4, px: 2 }}><Box><Skeleton width={44} height={15} sx={{ mb: 0.3 }} /><Skeleton width={52} height={10} /></Box></Box>
            <Box component="td" sx={{ py: 1.4, px: 2 }}><Skeleton width={70} height={12} /></Box>
            <Box component="td" sx={{ py: 1.4, px: 2 }}><Skeleton width={80} height={12} /></Box>
            <Box component="td" sx={{ py: 1.4, px: 2 }}><Skeleton width={55} height={12} /></Box>
            <Box component="td" sx={{ py: 1.4, px: 2 }}><Skeleton variant="rounded" width={60} height={6} sx={{ borderRadius: 1 }} /></Box>
            <Box component="td" sx={{ py: 1.4, px: 2 }}><Box><Skeleton width={56} height={13} sx={{ mb: 0.2 }} /><Skeleton width={70} height={9} /></Box></Box>
            <Box component="td" sx={{ py: 1.4, px: 2 }}><Skeleton width={50} height={13} /></Box>
            <Box component="td" sx={{ py: 1.4, px: 2 }}><Skeleton width={36} height={12} /></Box>
            <Box component="td" sx={{ py: 1.4, px: 2 }}><Box><Skeleton width={50} height={13} sx={{ mb: 0.2 }} /><Skeleton width={36} height={9} /></Box></Box>
            <Box component="td" sx={{ py: 1.4, px: 2 }}><Skeleton width={36} height={14} /></Box>
            <Box component="td" sx={{ py: 1.4, px: 2 }}><Skeleton variant="circular" width={20} height={20} /></Box>
          </Box>
        ))}
      </Box>
    </Box>
  </Box>
);